import { formatUnits } from '@ethersproject/units';
import { Grid } from '@mui/material';
import { useWeb3React } from '@web3-react/core';
import React, { useEffect } from 'react';

import { networkConfigs } from '../../constants';
import { ProtocolName } from '../../constants/protocolNames';
import { INCH_NATIVE_TOKEN_ADDRESS } from '../../constants/tokens';
import { useAppDispatch, useAppSelector, useUsdStablecoins } from '../../store';
import { selectSwapProtocol } from '../../store/state/swap/swapSlice';
import { SupportedChainId } from '../../types';
import { Field } from '../../types';
import { bigFloatToFixed } from '../../utils';
import SwapOptionItem from '../SwapOptionItem';

const SwapOptionsContainer = () => {
  const { chainId } = useWeb3React();
  const { defaultStablecoin } = useUsdStablecoins();
  const dispatch = useAppDispatch();
  const nativeToken = useAppSelector((state) => state.tokens.tokens[INCH_NATIVE_TOKEN_ADDRESS]);
  const nativeTokenUsdPrice = useAppSelector((state) => state.tokens.usdPrices[INCH_NATIVE_TOKEN_ADDRESS]);
  const loadingQuoteForAll = useAppSelector((state) => state.swap.loadingQuoteForAll);
  const toToken = useAppSelector((state) => state.tokens.tokens[state.swap[Field.OUTPUT]]);
  const selectedProtocol = useAppSelector((state) => state.swap.selectedProtocol);
  const typedValue = useAppSelector((state) => state.swap.typedValue);
  const quoteInfo = useAppSelector((state) => state.swap.swapData);
  const gasPrice = useAppSelector((state) => state.swap.txFeeCalculation.gasPriceInfo.price);
  const fees = useAppSelector((state) => state.swap.txFees);
  const supportedProtocols = chainId ? networkConfigs[chainId].supportedProtocols : [];

  useEffect(() => {
    if (!chainId || !supportedProtocols.length) return;
    const isAvailableMethod = supportedProtocols.includes(selectedProtocol);
    if (!isAvailableMethod) {
      dispatch(selectSwapProtocol(ProtocolName.ONE_INCH));
    }
  }, [chainId]);

  const createQuoteInfoLabel = (toTokenAmount?: string, decimals?: number) => {
    if (!toTokenAmount || !decimals) return '';
    return parseFloat(formatUnits(toTokenAmount, decimals)).toFixed(6);
  };

  const createTxCostLabel = (txFee?: string) => {
    if (!txFee || !defaultStablecoin || !nativeTokenUsdPrice) {
      return '';
    }
    const txCostInNativeToken = parseFloat(formatUnits(txFee, nativeToken.decimals));
    const nativeTokenPriceInUsd = parseFloat(formatUnits(nativeTokenUsdPrice, defaultStablecoin.decimals));
    const txCostInUsd = txCostInNativeToken * nativeTokenPriceInUsd;
    const getTokenSymbol = {
      [SupportedChainId.MAINNET]: 'Ξ',
      ['default']: nativeToken.symbol,
    };
    const tokenSymbol = getTokenSymbol[chainId as number] || getTokenSymbol['default'];
    return `~${txCostInNativeToken.toFixed(4)} ${tokenSymbol} (~$${bigFloatToFixed(txCostInUsd.toString(), 4)})`;
  };

  const createSwapOptionItemLabels = (
    loading: string,
    toTokenAmount?: string,
    toTokenDecimals?: number,
    txFee?: string
  ) => {
    if (!Number(typedValue) || loading !== 'succeeded' || !gasPrice) {
      return { txCost: '', quote: '' };
    }
    if (toTokenAmount === '0') {
      selectedProtocol !== ProtocolName.ONE_INCH && dispatch(selectSwapProtocol(ProtocolName.ONE_INCH));
      return { txCost: 'N/A', quote: 'N/A' };
    }

    return {
      txCost: createTxCostLabel(txFee),
      quote: createQuoteInfoLabel(toTokenAmount, toTokenDecimals),
    };
  };

  const oneInchItemLabels = createSwapOptionItemLabels(
    loadingQuoteForAll[ProtocolName.ONE_INCH]?.loadingStatus,
    quoteInfo[ProtocolName.ONE_INCH]?.toTokenAmount,
    toToken?.decimals,
    fees[ProtocolName.ONE_INCH]
  );

  // ADD PROTOCOL LABELS

  const createSwapOptionClickHandler = (protocolName: string) => {
    return () => {
      if (protocolName !== selectedProtocol) {
        dispatch(selectSwapProtocol(protocolName));
      }
    };
  };

  const swapOptionItems: { [protocolName: string]: any } = {
    [ProtocolName.ONE_INCH]: (
      <SwapOptionItem
        optionName={ProtocolName.ONE_INCH}
        onClick={createSwapOptionClickHandler(ProtocolName.ONE_INCH)}
        isBestQuote={ProtocolName.ONE_INCH === selectedProtocol}
        quoteLabel={oneInchItemLabels.quote}
        txCostLabel={oneInchItemLabels.txCost}
      />
    ),
    //  ADD PROTOCOLS HERE
  };

  return (
    <Grid container direction="column" rowGap={1.7}>
      {supportedProtocols.length &&
        supportedProtocols.map((protocol: string, index: number) => (
          <Grid item key={index}>
            {swapOptionItems[protocol]}
          </Grid>
        ))}
    </Grid>
  );
};

export default SwapOptionsContainer;
